<script setup lang="ts">
import { computed, ref, watch } from "vue";
import VButton from "@magnit/core/src/components/VButton/VButton.vue";
import { useElementSize } from "@vueuse/core";
import ArrowLeft from "@magnit/icons/src/assets/icons/arrow-left-full-16.svg";
import ArrowRight from "@magnit/icons/src/assets/icons/arrow-right-full-16.svg";
import type { IPaginationEmits, IPaginationProps } from "./VPagination.types";

const props = withDefaults(defineProps<IPaginationProps>(), {
  total: 0,
  page: 0,
  pageSize: 0,
  showMoreLabel: "Показать ещё",
  showMoreVisible: true,
  showPages: true,
});
const emit = defineEmits<IPaginationEmits>();

const VIEWPORT_WIDTH_L = 960;
const PAGER_COUNT_MOBILE = 5;
const PAGER_COUNT_WEB_SMALL = 6;
const PAGER_COUNT_WEB_MIDDLE = 7;
const PAGER_COUNT_WEB_LARGE = 8;

const el = ref(null);
const showPrevMore = ref(false);
const showNextMore = ref(false);
const { width } = useElementSize(el);
const isWeb = computed(() => width.value >= VIEWPORT_WIDTH_L);
const showMore = computed(
  () => props.showMoreVisible && props.page < pageCount.value,
);
const showPagination = computed(
  () => pageCount.value > 1 && (props.showPages || showMore.value),
);
const showPrevArrow = computed(
  () =>
    props.page > 1 && isWeb.value && pageCount.value > PAGER_COUNT_WEB_LARGE,
);
const showNextArrow = computed(
  () =>
    props.page < pageCount.value &&
    isWeb.value &&
    pageCount.value > PAGER_COUNT_WEB_LARGE,
);
const pagerCount = computed(() => {
  if (!isWeb.value) return PAGER_COUNT_MOBILE;
  if (showPrevMore.value != showNextMore.value) return PAGER_COUNT_WEB_SMALL;
  if (pageCount.value <= PAGER_COUNT_WEB_LARGE) return PAGER_COUNT_WEB_LARGE;
  return PAGER_COUNT_WEB_MIDDLE;
});
const pageCount = computed<number>(() =>
  props.pageSize ? Math.ceil(props.total / props.pageSize) : 0,
);
const pagers = computed(() => {
  const halfPagerCount = (pagerCount.value - 1) / 2;

  let showPrevMore = false;
  let showNextMore = false;
  if (pageCount.value > pagerCount.value) {
    if (props.page > pagerCount.value - halfPagerCount) {
      showPrevMore = true;
    }
    if (props.page < pageCount.value - halfPagerCount) {
      showNextMore = true;
    }
  }
  const array: number[] = [];
  let start = 2;
  let end = pageCount.value;
  if (showPrevMore && !showNextMore) {
    start = pageCount.value - (pagerCount.value - 2);
  } else if (!showPrevMore && showNextMore) {
    end = pagerCount.value;
  } else if (showPrevMore && showNextMore) {
    start = props.page - Math.floor((pagerCount.value - 4) / 2) - 1;
    end = props.page + Math.ceil((pagerCount.value - 4) / 2) + 1;
  }

  for (let i = start; i < end; i++) {
    array.push(i);
  }

  return array;
});

const changePage = (newPage: number) => {
  if (props.page === newPage) return;
  emit("pagination:set-page", newPage);
};
const onShowMoreClick = () => emit("pagination:show-more");

const setShowMoreValues = () => {
  const halfPagerCount = (pagerCount.value - 1) / 2;
  showPrevMore.value = false;
  showNextMore.value = false;
  if (pageCount.value > pagerCount.value) {
    if (props.page > pagerCount.value - halfPagerCount) {
      showPrevMore.value = true;
    }
    if (props.page < pageCount.value - halfPagerCount) {
      showNextMore.value = true;
    }
  }
};

watch(
  () => props.page,
  () => {
    setShowMoreValues();
  },
);
watch(
  pagerCount,
  () => {
    setShowMoreValues();
  },
  { immediate: true },
);
</script>

<template>
  <div v-if="showPagination" ref="el" class="pl-pagination">
    <VButton
      v-if="showMore"
      class="pl-pagination__show-more"
      :loading="isLoading"
      :disabled="isLoading"
      full-width
      theme="secondary"
      @click="onShowMoreClick()"
    >
      {{ showMoreLabel }}
    </VButton>
    <nav
      v-if="showPages"
      aria-label="pagination"
      class="pl-pagination__pager"
      :class="[{ 'pl-pagination_disabled': isLoading }]"
    >
      <ul>
        <li v-if="showPrevArrow">
          <VButton
            theme="border"
            :disabled="isLoading"
            @click="changePage(page - 1)"
          >
            <template #icon>
              <ArrowLeft />
            </template>
          </VButton>
        </li>
        <li v-if="pageCount > 0" :aria-current="page === 1">
          <VButton
            :theme="page === 1 ? 'primary' : 'secondary'"
            :disabled="isLoading"
            @click="changePage(1)"
          >
            <template #icon>1</template>
          </VButton>
        </li>
        <li v-if="showPrevMore" class="pl-pagination__dots">...</li>
        <li v-for="pager in pagers" :key="pager" :aria-current="page === pager">
          <VButton
            :theme="page === pager ? 'primary' : 'secondary'"
            :disabled="isLoading"
            @click="changePage(pager)"
          >
            <template #icon>{{ pager }}</template>
          </VButton>
        </li>
        <li v-if="showNextMore" class="pl-pagination__dots">...</li>
        <li v-if="pageCount > 1" :aria-current="page === pageCount">
          <VButton
            :theme="page === pageCount ? 'primary' : 'secondary'"
            :disabled="isLoading"
            @click="changePage(pageCount)"
          >
            <template #icon>{{ pageCount }}</template>
          </VButton>
        </li>
        <li v-if="showNextArrow">
          <VButton
            theme="border"
            border
            :disabled="isLoading"
            @click="changePage(page + 1)"
          >
            <template #icon>
              <ArrowRight />
            </template>
          </VButton>
        </li>
      </ul>
    </nav>
  </div>
</template>

<style lang="postcss" scoped>
.pl-pagination {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--pl-unit-x4);
  padding-top: var(--pl-unit-x6);

  &__show-more {
    @media (--pl-viewport-from-l) {
      max-width: 408px;
    }
  }

  &__pager {
    ul {
      display: flex;
      justify-content: center;
      gap: var(--pl-unit-x2);
      list-style-type: none;
      padding: 0;
      margin: 0;
    }
  }

  &__dots {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    aspect-ratio: 1;
  }

  &_disabled {
    .pl-pagination__dots {
      color: var(--pl-text-tertiary);
    }
  }
}
</style>
